import React from 'react';
import { adminApi } from '../../api';
import { useApi } from '../../hooks/useApi';
import { Spinner } from '../../components/StateHelpers';
import { Package, ServerCog, GitCommit, Gift, HelpCircle, MessageSquare, Mail, ShoppingBag, Users, FileText, Activity, Clock, CalendarDays, Calendar } from 'lucide-react';

const CARDS = [
  { k: 'products', l: 'Ürün', icon: Package, color: 'text-[#f4d896]' },
  { k: 'servers', l: 'Sunucu', icon: ServerCog, color: 'text-blue-300' },
  { k: 'project_updates', l: 'Güncelleme', icon: GitCommit, color: 'text-emerald-300' },
  { k: 'free_products', l: 'Ücretsiz Ürün', icon: Gift, color: 'text-emerald-300' },
  { k: 'faqs', l: 'SSS', icon: HelpCircle, color: 'text-white/70' },
  { k: 'contact_messages', l: 'Mesaj', icon: MessageSquare, color: 'text-pink-300' },
  { k: 'mailing_list', l: 'Aboneler', icon: Mail, color: 'text-[#f4d896]' },
  { k: 'orders', l: 'Sipariş', icon: ShoppingBag, color: 'text-purple-300' },
  { k: 'users', l: 'Kullanıcı', icon: Users, color: 'text-blue-300' },
  { k: 'blog_posts', l: 'Blog', icon: FileText, color: 'text-white/70' },
];

export default function AdminDashboard() {
  const { data, loading } = useApi(() => Promise.all([adminApi.stats(), adminApi.analytics()]).then(([stats, analytics]) => ({ ...stats, analytics })), []);

  return (
    <div>
      <h1 className="font-display text-3xl font-black mb-2">Dashboard</h1>
      <p className="text-white/50 mb-8">Site özeti — hızlı istatistikler ve ziyaretçi analitiği.</p>

      {loading ? <Spinner /> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Clock className="w-16 h-16 text-blue-400" /></div>
              <div className="flex items-center gap-2 mb-3"><Clock className="w-4 h-4 text-blue-400" /><span className="text-[10px] text-white/40 tracking-widest">SON 24 SAAT (TEKİL)</span></div>
              <div className="font-display text-4xl font-black text-white">{(data?.analytics?.unique_visitors?.['24h'] || 0).toLocaleString('tr-TR')}</div>
            </div>
            <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><CalendarDays className="w-16 h-16 text-emerald-400" /></div>
              <div className="flex items-center gap-2 mb-3"><CalendarDays className="w-4 h-4 text-emerald-400" /><span className="text-[10px] text-white/40 tracking-widest">SON 7 GÜN (TEKİL)</span></div>
              <div className="font-display text-4xl font-black text-white">{(data?.analytics?.unique_visitors?.['7d'] || 0).toLocaleString('tr-TR')}</div>
            </div>
            <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Calendar className="w-16 h-16 text-orange-400" /></div>
              <div className="flex items-center gap-2 mb-3"><Calendar className="w-4 h-4 text-orange-400" /><span className="text-[10px] text-white/40 tracking-widest">SON 30 GÜN (TEKİL)</span></div>
              <div className="font-display text-4xl font-black text-white">{(data?.analytics?.unique_visitors?.['30d'] || 0).toLocaleString('tr-TR')}</div>
            </div>
            <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10"><Activity className="w-16 h-16 text-purple-400" /></div>
              <div className="flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-purple-400" /><span className="text-[10px] text-white/40 tracking-widest">TÜM ZAMANLAR (TEKİL)</span></div>
              <div className="font-display text-4xl font-black text-white">{(data?.analytics?.unique_visitors?.['all'] || 0).toLocaleString('tr-TR')}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
            {CARDS.map((c) => (
              <div key={c.k} className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-5">
                <div className="flex items-center justify-between mb-3">
                  <c.icon className={`w-5 h-5 ${c.color}`} />
                  <span className="text-[10px] text-white/40 tracking-widest">TOPLAM</span>
                </div>
                <div className="font-display text-3xl font-black text-white">{(data?.counts?.[c.k] || 0).toLocaleString('tr-TR')}</div>
                <div className="text-xs text-white/50 mt-1">{c.l}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4">Son İletişim Mesajları</h3>
              {(data?.recent_messages || []).length === 0 ? <p className="text-white/50 text-sm">Henüz mesaj yok.</p> : (
                <div className="space-y-3">
                  {data.recent_messages.map((m) => (
                    <div key={m.id} className="p-4 rounded-lg border border-white/8 bg-white/[0.02] flex items-start justify-between gap-4">
                      <div className="min-w-0"><div className="font-semibold text-white">{m.subject}</div><div className="text-xs text-white/50 mt-0.5">{m.name} · {m.email}</div><p className="text-sm text-white/60 mt-2 line-clamp-2">{m.message}</p></div>
                      <div className="text-[10px] text-white/40 whitespace-nowrap">{String(m.created_at).slice(0, 10)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="rounded-2xl border border-white/8 bg-gradient-to-b from-[#08080b] to-[#08080b] p-6">
              <h3 className="font-display text-xl font-bold text-white mb-4">En Çok Ziyaret Edilen Sayfalar</h3>
              {(data?.analytics?.top_pages || []).length === 0 ? <p className="text-white/50 text-sm">Henüz ziyaret verisi yok.</p> : (
                <div className="space-y-2">
                  {data.analytics.top_pages.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                      <div className="text-sm font-medium text-white/80 truncate pr-4">{p.path}</div>
                      <div className="text-xs font-bold text-[#e4b44e] bg-[#e4b44e]/10 px-2 py-1 rounded-md">{p.views.toLocaleString('tr-TR')} <span className="font-normal text-[#e4b44e]/60">ziyaret</span></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

